import { useState } from "react";
import { Send } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SendToProject from "@/components/recstudio/SendToProject";
import SendToMySong from "@/components/recstudio/SendToMySong";
import { channelOf } from "@/components/recstudio/sessionEngine";
import { renderWithFx, toMono } from "@/components/recstudio/takeRender";
import { encodeWav } from "@/lib/wavEncoder";
import { safeName } from "@/lib/audioExport";

/**
 * "Send to song" for one take (or a whole choir stack): render it (dry, or with its vocal sound) as a WAV that starts
 * at the top of the music, then hand it to a song project or a song in My Songs.
 * `source` = { label, stereo, buffer: () => Promise<AudioBuffer> }.
 */
export default function SendToSongDialog({ open, onClose, session, track, source }) {
  const { doc, engine } = session;
  const idx = track ? channelOf(engine, doc.id, track.id) : null;
  const hasFx = idx != null && Object.values(engine.channels[idx].fx || {}).some(Boolean);
  const [withFx, setWithFx] = useState(true);
  const makeFile = async () => {
    const buf = await source.buffer();
    const out = withFx && hasFx ? await renderWithFx(engine, idx, buf) : source.stereo ? buf : toMono(engine.ctx, buf);
    return new File([encodeWav(out)], `${safeName(`${doc.title} - ${source.label}`)}.wav`, { type: "audio/wav" });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="rec-send-dialog" className="max-w-lg border-white/10 bg-[#0b0e1c] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Send className="h-4 w-4 text-rose-200" /> Send to a song</DialogTitle>
          <DialogDescription data-testid="rec-send-source" className="text-white/60">{track?.name} · {source?.label}</DialogDescription>
        </DialogHeader>
        <label className={`flex items-center gap-2 text-[12px] ${hasFx ? "cursor-pointer text-white/80" : "text-white/35"}`}>
          <input type="checkbox" data-testid="rec-send-fx" checked={withFx && hasFx} disabled={!hasFx} onChange={(e) => setWithFx(e.target.checked)} className="h-4 w-4 accent-rose-400" />
          Include my vocal sound (effects){hasFx ? "" : " — this track has none on"}
        </label>
        <Tabs defaultValue="project">
          <TabsList className="grid w-full grid-cols-2 bg-white/[0.05]">
            <TabsTrigger value="project" data-testid="rec-send-tab-project">Song project</TabsTrigger>
            <TabsTrigger value="song" data-testid="rec-send-tab-song">My Songs</TabsTrigger>
          </TabsList>
          <TabsContent value="project"><SendToProject makeFile={makeFile} instrumental={doc.instrumental} /></TabsContent>
          <TabsContent value="song"><SendToMySong makeFile={makeFile} instrumental={doc.instrumental} label={source?.label || "my voice"} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
